/**
 * Activity-types step of the unified import flow.
 *
 * Asks the user which activity types their spreadsheet contains. The selection
 * is written to `activityTypes` on the shared React Hook Form context and sent
 * to the backend on the upload step, which decides how to handle the import.
 *
 * "Other goods" is a catch-all and cannot be selected on its own — it is
 * disabled until Food or Packaging is ticked, and auto-cleared if the user
 * removes every primary type.
 */
import { useImport } from '@app/components/imports/import-layout';
import { getGoodsImportRoutes } from '@app/components/imports/goods-import/goods-import-routes';
import { ActivityType } from '@app/components/imports/utils';
import Page from '@app/components/layout/page/page';
import { OperationReportTypeEnum } from '@shared/api/types';
import Text from '@shared/components/content/text';
import CheckboxMultipleFormField from '@shared/components/form/checkbox-multiple-form-field';
import GuideTemplate from '@shared/components/guide-template';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  getActivityTypeOptions,
  isPrimaryActivityType,
} from '../activity-type-options';

export default function GoodsImportTypePage() {
  const { reportType } = useImport();
  const { watch, setValue } = useFormContext();
  const navigate = useNavigate();

  const routes = getGoodsImportRoutes(reportType);

  const watchedActivityTypes = watch('activityTypes');
  const activityTypes = useMemo<ActivityType[]>(
    () => watchedActivityTypes ?? [],
    [watchedActivityTypes]
  );
  const hasPrimary = activityTypes.some(isPrimaryActivityType);

  // "Other goods" can only ride along with a primary type — clear it if the
  // user deselects every primary.
  useEffect(() => {
    if (!hasPrimary && activityTypes.includes('other')) {
      setValue(
        'activityTypes',
        activityTypes.filter((type) => type !== 'other')
      );
    }
  }, [hasPrimary, activityTypes, setValue]);

  const isSales = reportType === OperationReportTypeEnum.sales;

  const options = getActivityTypeOptions(isSales).map((option) =>
    option.value === 'other' ? { ...option, disabled: !hasPrimary } : option
  );

  // No RHF handleSubmit here: the later steps own the period / site fields the
  // schema validates, so we just gate on a primary selection and navigate.
  const next = () => navigate(routes.organise);

  return (
    <Page name="Import - Activity types">
      <GuideTemplate
        className="w-full overflow-visible"
        preTitle={`Import ${isSales ? 'sales' : 'purchases'}`}
        title="Select goods types"
        description={
          <Text>
            Tell us which types of items are included in your import. Our import
            tool can automatically detect each type of item and model them
            accordingly, but you will get the best performance importing just a
            single type of goods.
          </Text>
        }
        primaryActionProps={{
          disabled: !hasPrimary,
          onClick: next,
          'data-testid': 'activity-types-next',
        }}
        primaryActionText="Next"
        secondaryActionText="Back"
        secondaryActionProps={{
          onClick: () => navigate(routes.landing),
        }}
      >
        <div className="mx-auto w-[560px] max-w-full text-left">
          {/* Each option is its own selectable card/button; icons top-aligned.
              Selected styling mirrors the old ImporterModeCard: a 2px teal
              border that flips to orange (border only, no fill). */}
          <CheckboxMultipleFormField
            name="activityTypes"
            options={options}
            optionClassName="items-start border-[3px] border-teal-100 bg-gray-00 py-4 shadow-sm hover:border-orange-200 data-[state=checked]:border-orange-300 data-[state=checked]:bg-gray-00"
          />
        </div>
      </GuideTemplate>
    </Page>
  );
}
